import React, { useState, FormEvent, ChangeEvent } from "react";
import classnames from "classnames";
import { OutboundLink } from "./OutboundLink";

const THE_CITY_FALLBACK_NEWSLETTER_LINK =
  "https://www.thecityreporter.nyc/the-scoop/";

export type RequestStatus = "idle" | "loading" | "success" | "error";

export const NewsletterSignupBanner: React.FC<{
  isOnLandingPage?: boolean;
}> = ({ isOnLandingPage }) => {
  const [email, setEmail] = useState<string>("");
  const [statusTheCity, setStatusTheCity] = useState<RequestStatus>("idle");

  /**
   * Sign up for The City Reporter's Scoop newsletter via Netlify email proxy
   */
  const submitTheCity = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatusTheCity("loading");
    try {
      const response = await fetch(
        "https://thescoop-proxy.netlify.app/.netlify/functions/subscribe",
        {
          method: "POST",
          headers: {
            accept: "application/json",
            "content-type": "application/json",
          },
          body: JSON.stringify({
            email,
          }),
        },
      );

      if (response.ok) {
        setStatusTheCity("success");
        setEmail("");
      } else {
        setStatusTheCity("error");
      }
    } catch (error) {
      setStatusTheCity("error");
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setStatusTheCity("loading");

    submitTheCity(e);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  return (
    <div className="newsletter-signup">
      <div
        className={classnames("container", "py-4", isOnLandingPage && "px-4")}
        style={{ maxWidth: "475px" }}
      >
        <form
          onSubmit={handleSubmit}
          className={classnames(
            "is-flex",
            !isOnLandingPage && "is-justify-content-center",
          )}
        >
          <div className="field">
            <div
              className={classnames(
                "eyebrow",
                "mb-2",
                !isOnLandingPage && "has-text-centered",
              )}
            >
              Sign up and get the latest stories from The City Reporter
              delivered to you each morning:
            </div>
            <div
              className={classnames(
                "is-flex",
                !isOnLandingPage && "is-align-items-center",
              )}
            >
              <div className="control mr-3 is-flex-grow-1">
                <input
                  className="input is-small"
                  type="email"
                  aria-label="Input your email for The City Reporter's newsletter"
                  placeholder="e.g. name@example.com"
                  value={email}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="control">
                <button
                  className="button is-outline eyebrow"
                  style={{ color: "black" }}
                  type="submit"
                  disabled={statusTheCity === "loading"}
                >
                  {statusTheCity === "loading" ? (
                    <>
                      Submitting
                      <span className="dot">.</span>
                      <span className="dot">.</span>
                      <span className="dot">.</span>
                    </>
                  ) : (
                    <>Sign Up</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>
        {statusTheCity === "success" ? (
          <p
            className={classnames(
              "label mt-2 has-text-light",
              !isOnLandingPage && "has-text-centered",
            )}
          >
            You're signed up!
          </p>
        ) : statusTheCity === "error" ? (
          <p
            className={classnames(
              "eyebrow has-text-weight-light mt-3 has-text-light",
              !isOnLandingPage && "has-text-centered",
            )}
          >
            Something went wrong. Sign up manually via{" "}
            <OutboundLink
              to={THE_CITY_FALLBACK_NEWSLETTER_LINK}
              className="has-text-primary"
            >
              The City Reporter
            </OutboundLink>
            .
          </p>
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};
